import React, { lazy, Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import SEOHead from '../seo/SEOHead';
import { stripAptSeoJsonLd } from '../../utils/stripAptSeoJsonLd';
import { generateWebSiteSchema } from '../../utils/structuredData';
import SearchHero from './SearchHero';
import HostTrainingSection from './HostTrainingSection';
import UpcomingTrainingSection from './UpcomingTrainingSection';
import PathCardsSection from './PathCardsSection';
import BrowseCategoriesSection from './BrowseCategoriesSection';
import TrustPointsSection from './TrustPointsSection';
import { UnifiedEventService } from '../../services/unifiedEventService';
import type { EventCardData } from '../events/eventCardTypes';
import {
  DEFAULT_HOST_TRAINING,
  DEFAULT_PATH_CARDS,
  DEFAULT_POPULAR_SEARCHES_BY_TYPE,
  DEFAULT_SEARCH_HERO,
  HOMEPAGE_SEARCH_TYPES,
  type HostTrainingContent,
  type HomepagePopularSearch,
  type HomepageSearchType,
  type PathCardContent,
  type SearchHeroContent,
} from '../../../shared/homepage/searchFirstDefaults';
import { HOMEPAGE_SECTION_PY } from '../../../shared/homepage/homepageLayout';
import { logger } from '../../utils/logger';
import { loadHomepageLayoutOnce } from '../../utils/homepageLayoutLoader';
import { logComponentComplete, logComponentStart } from '../../utils/diagnosticLogger';
import { AGENCY_SHELL_HORIZONTAL_PX } from '../../constants/layoutSpacing';
import FeaturedVendorsSection from '../vendors/FeaturedVendorsSection';

const BlogModule = lazy(() => import('./BlogModule'));

type SectionVisibility = Record<string, boolean>;

function buildDefaultPopularByType(): Record<HomepageSearchType, HomepagePopularSearch[]> {
  return Object.fromEntries(
    HOMEPAGE_SEARCH_TYPES.map((type) => [
      type,
      DEFAULT_POPULAR_SEARCHES_BY_TYPE[type].map((p, i) => ({
        id: `${type}-${i}`,
        label: p.label,
        query: p.query,
        sort_order: p.sort_order,
      })),
    ]),
  ) as Record<HomepageSearchType, HomepagePopularSearch[]>;
}

const HomePageSearchFirst: React.FC = () => {
  logComponentStart('HomePageSearchFirst', { timestamp: new Date().toISOString() });

  const [visibility, setVisibility] = useState<SectionVisibility>({
    search_hero: true,
    host_training: true,
    featured_events: true,
    path_cards: true,
    explore_bar: true,
    recent_published_blog: true,
  });
  const [searchHero, setSearchHero] = useState<SearchHeroContent>(DEFAULT_SEARCH_HERO);
  const [popularSearchesByType, setPopularSearchesByType] = useState<
    Record<HomepageSearchType, HomepagePopularSearch[]>
  >(buildDefaultPopularByType);
  const [loadingPopular, setLoadingPopular] = useState(true);
  const [hostTraining, setHostTraining] = useState<HostTrainingContent>(DEFAULT_HOST_TRAINING);
  const [pathCards, setPathCards] = useState<PathCardContent[]>(DEFAULT_PATH_CARDS);
  const [pathTitle, setPathTitle] = useState('Platform Benefits');
  const [pathSubtitle, setPathSubtitle] = useState(
    'Everything you need to find, host, and deliver training',
  );
  const [browseHeading, setBrowseHeading] = useState('Browse by Category');
  const [browseLinks, setBrowseLinks] = useState<
    Array<{ id: string; label: string; href: string; open_new_tab: boolean }>
  >([]);
  const [events, setEvents] = useState<EventCardData[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let loadGeneration = 0;

    const loadEvents = async (generation: number) => {
      try {
        const upcoming = await UnifiedEventService.getHomepageUpcomingEvents(3);
        if (cancelled || generation !== loadGeneration) {
          return;
        }
        setEvents(Array.isArray(upcoming) ? upcoming.slice(0, 3) : []);
      } catch (error) {
        logger.error('HomePageSearchFirst events load error:', error, {});
        if (!cancelled && generation === loadGeneration) {
          setEvents([]);
        }
      } finally {
        if (!cancelled && generation === loadGeneration) {
          setEventsLoading(false);
        }
      }
    };

    const load = async () => {
      const generation = ++loadGeneration;
      void loadEvents(generation);

      try {
        const [layoutJson, exploreRes] = await Promise.all([
          loadHomepageLayoutOnce(),
          fetch('/api/homepage/explore-bar'),
        ]);

        const d = layoutJson?.data;
        if (d?.visibility && typeof d.visibility === 'object') {
          setVisibility((prev) => ({ ...prev, ...d.visibility }));
        }
        if (d?.searchHero && typeof d.searchHero === 'object') {
          const sh = d.searchHero as Partial<SearchHeroContent> & {
            popularSearches?: HomepagePopularSearch[];
            popularSearchesByType?: Partial<Record<HomepageSearchType, HomepagePopularSearch[]>>;
          };
          setSearchHero({
            brand: sh.brand || DEFAULT_SEARCH_HERO.brand,
            mission: sh.mission || DEFAULT_SEARCH_HERO.mission,
            support: sh.support || DEFAULT_SEARCH_HERO.support,
            placeholder: sh.placeholder || DEFAULT_SEARCH_HERO.placeholder,
          });
          const next = buildDefaultPopularByType();
          if (sh.popularSearchesByType && typeof sh.popularSearchesByType === 'object') {
            for (const type of HOMEPAGE_SEARCH_TYPES) {
              const list = sh.popularSearchesByType[type];
              if (Array.isArray(list) && list.length > 0) {
                next[type] = list;
              }
            }
          } else if (Array.isArray(sh.popularSearches) && sh.popularSearches.length > 0) {
            next.events = sh.popularSearches;
          }
          setPopularSearchesByType(next);
        }
        if (d?.hostTraining && typeof d.hostTraining === 'object') {
          setHostTraining({ ...DEFAULT_HOST_TRAINING, ...(d.hostTraining as HostTrainingContent) });
        }
        if (d?.pathCards && typeof d.pathCards === 'object') {
          const pc = d.pathCards;
          if (Array.isArray(pc.cards)) {
            setPathCards(pc.cards as PathCardContent[]);
          }
          if (pc.title) setPathTitle(String(pc.title));
          if (pc.subtitle) setPathSubtitle(String(pc.subtitle));
        }

        if (exploreRes?.ok) {
          const exploreJson = await exploreRes.json();
          const ed = exploreJson?.data;
          if (ed?.heading) setBrowseHeading(ed.heading);
          if (Array.isArray(ed?.links)) {
            setBrowseLinks(
              ed.links.map((l: { id: string; label: string; href: string; open_new_tab?: boolean }) => ({
                id: String(l.id),
                label: l.label,
                href: l.href,
                open_new_tab: Boolean(l.open_new_tab),
              })),
            );
          }
          if (ed && typeof ed.visible === 'boolean') {
            setVisibility((prev) => ({ ...prev, explore_bar: ed.visible }));
          }
        }
      } catch (error) {
        logger.error('HomePageSearchFirst load error:', error, {});
      } finally {
        if (!cancelled) {
          setLoadingPopular(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      loadGeneration += 1;
    };
  }, []);

  logComponentComplete('HomePageSearchFirst');

  const homeWebSiteStructuredData = [
    generateWebSiteSchema({
      name: 'American Police Training',
      url: 'https://americanpolicetraining.com/',
      description: 'The national platform for law enforcement training.',
      searchUrlTemplate:
        'https://americanpolicetraining.com/events?search={search_term_string}',
    }),
  ];

  useLayoutEffect(() => {
    stripAptSeoJsonLd();
  }, []);

  return (
    <>
      <SEOHead
        title="American Police Training — The National Platform for Law Enforcement Training"
        description="Search verified law enforcement training, discover instructors, host courses, and manage registrations—all in one place."
        keywords="police training, law enforcement training, SFST, FTO, find training events, host police training"
        canonical="https://americanpolicetraining.com/"
        ogType="website"
        structuredData={homeWebSiteStructuredData}
      />

      {visibility.search_hero !== false && (
        <SearchHero
          content={searchHero}
          popularSearchesByType={popularSearchesByType}
          popularSearches={popularSearchesByType.events}
          loadingPopular={loadingPopular}
        />
      )}

      {visibility.host_training !== false && <HostTrainingSection content={hostTraining} />}

      {visibility.featured_events !== false && (
        <UpcomingTrainingSection events={events} loading={eventsLoading} />
      )}

      <TrustPointsSection />

      {visibility.explore_bar !== false && (
        <BrowseCategoriesSection heading={browseHeading} links={browseLinks} useTopicDefaults />
      )}

      {visibility.path_cards !== false && (
        <PathCardsSection title={pathTitle} subtitle={pathSubtitle} cards={pathCards} />
      )}

      <Box sx={{ py: HOMEPAGE_SECTION_PY.articles, px: AGENCY_SHELL_HORIZONTAL_PX }}>
        <FeaturedVendorsSection />
      </Box>

      {visibility.recent_published_blog !== false && (
        <Box
          sx={{
            py: HOMEPAGE_SECTION_PY.articles,
            px: AGENCY_SHELL_HORIZONTAL_PX,
            bgcolor: 'background.default',
            // Reserve article grid height while BlogModule chunk loads (avoids footer shove).
            minHeight: { xs: 520, md: 480 },
          }}
        >
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: { xs: 400, md: 360 },
                }}
              >
                <CircularProgress aria-label="Loading articles" />
              </Box>
            }
          >
            <BlogModule forceDisplay maxPosts={3} hideExcerpt />
          </Suspense>
        </Box>
      )}
    </>
  );
};

export default HomePageSearchFirst;
